import axios, {
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { jwtDecode } from "jwt-decode";

import { Authentication as AuthenticationType } from "../types/authen";
import { fetcher } from "./fetch";

interface DecodedToken {
  exp: number;
}

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;

const defaultHeaders = {
  "Content-Type": "application/json",
  // "Cache-Control": "no-cache",
};

const getUser = (): any | null => {
  let user = null;
  if (localStorage.getItem("user")) {
    user = JSON.parse(localStorage.getItem("user")!);
  }
  return user;
};

const getAccessToken = (): any | null => {
  const user = getUser();
  const accessToken = user?.state?.user?.access_token
    ? user?.state?.user?.access_token
    : null;
  return accessToken;
};

const setUserWithAccessToken = (user: any) => {
  localStorage.setItem("user", JSON.stringify({ state: { user } }));
};

const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const response: AuthenticationType = await fetcher(
      "POST",
      "refresh-token",
      null,
      null,
      null,
      true,
    );

    let user: AuthenticationType | null = null;
    let newAccessToken = null;
    if (response) {
      user = {
        ...response,
        expire_at:
          jwtDecode<DecodedToken>(response?.access_token).exp * 1000 - 15000,
      };
      newAccessToken = response?.access_token;
    }
    setUserWithAccessToken(user);
    return newAccessToken;
  } catch (error) {
    console.error("Failed to refresh access token:", error);
    localStorage.removeItem("user");
    window.location.href = "/";
    return null;
  }
};

export const checkRefreshTokenWithExpire = async () => {
  const userState = getUser();
  const user = userState?.state?.user || null;
  const currentTime: number = Date.now();

  if (currentTime > user?.expire_at) {
    await refreshAccessToken();
  }
};

// Create an instance of axios with default settings
const axiosInstance = axios.create({
  baseURL: API_ENDPOINT, // Set your base URL here
  // timeout: 10000, // Set a timeout for requests
  headers: defaultHeaders,
  paramsSerializer: {
    indexes: null, // serialize arrays as ?key=val1&key=val2 (no brackets)
  },
});

// Request interceptor to add access token to headers
axiosInstance.interceptors.request.use(
  (config: AxiosRequestConfig): any => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token refresh
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    await checkRefreshTokenWithExpire();

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const newAccessToken = await refreshAccessToken();
      if (newAccessToken) {
        axios.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      }
    }

    return Promise.reject(error);
  },
);

// Function to handle GET requests
export const authFetcher = async <T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  url: string,
  data: any = null,
  params: Record<string, any> | null = null,
  customHeaders: Record<string, any> | null = null,
  withCredentials: boolean = false,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  signal?: any,
  responseType?: "json" | "blob",
): Promise<T | any> => {
  try {
    // const combinedHeaders = {
    //   ...axiosInstance.defaults.headers.common,
    //   ...defaultHeaders,
    //   ...customHeaders,
    // };

    let combinedHeaders = {};
    if (customHeaders) {
      combinedHeaders = {
        ...axiosInstance.defaults.headers.common,
        ...customHeaders,
      };
    } else {
      combinedHeaders = {
        ...axiosInstance.defaults.headers.common,
        ...defaultHeaders,
      };
    }

    const config: AxiosRequestConfig = {
      method,
      url,
      params,
      headers: combinedHeaders,
      onUploadProgress,
      signal,
      responseType,
    };

    if (withCredentials) {
      config.withCredentials = withCredentials;
    }

    if (data) {
      config.data = data;
    }

    const response: AxiosResponse<T> = await axiosInstance.request(config);
    return responseType == "blob" ? response : response.data;
  } catch (error) {
    console.error("Request error:", error);
    throw error;
  }
};
